/**
 * Batched profile and relay-list loading.
 *
 * The naive approach does not survive contact with a relay: relays cap concurrent
 * subscriptions (`max_subscriptions` in NIP-11 is often 20), and a timeline of 50
 * notes by 40 authors asking for each author's profile as it scrolls into view is
 * 40 subscriptions the relay will simply refuse.
 *
 * So all demand across the entire app funnels through one debounced, chunked,
 * de-duplicated queue:
 *  - **debounce** so a screen full of avatars mounting produces one request;
 *  - **chunk** so no single filter exceeds a relay's author limit;
 *  - **dedupe** against what is already stored and already in flight;
 *  - **cool down** failures, so a pubkey with no discoverable profile is not
 *    re-requested on every render forever.
 */

import type { Hex32, RelayBasedFilter } from "@setu/protocol";
import type {
  EventStore,
  ProfileBatcher,
  SubscriptionManager,
} from "../contracts";
import { normalizeRelayUrls } from "../relay/normalize";
import type { OutboxRouter } from "../relay/outboxRouter";
import { KIND_METADATA, KIND_RELAY_LIST } from "../store/kinds";

/** Construction options for {@link DefaultProfileBatcher}. */
export interface ProfileBatcherOptions {
  readonly store: EventStore;
  readonly subscriptions: SubscriptionManager;
  /** Relays used when no router is supplied, or when routing yields nothing. */
  readonly relays: readonly string[];
  /** Optional outbox router; profiles are best fetched from the author's relays. */
  readonly router?: OutboxRouter;
  /** Debounce window in ms. Default 250. */
  readonly debounceMs?: number;
  /** Max authors per filter. Default 100. */
  readonly maxAuthorsPerFilter?: number;
  /** How long before a pubkey that yielded nothing may be retried. Default 60s. */
  readonly failureCooldownMs?: number;
  /**
   * How long before a pubkey that yielded *some* of the requested kinds may be
   * retried. Default 15 minutes.
   *
   * Longer than the failure cooldown on purpose: the usual partial result is
   * metadata without a relay list, and an author who has never published one will
   * answer the same way every time.
   */
  readonly partialCooldownMs?: number;
  /** Kinds requested per pubkey. Default `[0, 10002]`. */
  readonly kinds?: readonly number[];
  /** Millisecond clock, injectable for tests. */
  readonly now?: () => number;
  readonly onError?: (error: unknown) => void;
}

/** Queue and cache counters, for a debug UI. */
export interface ProfileBatcherStats {
  /** Pubkeys whose metadata we hold. */
  readonly loaded: number;
  /** Pubkeys queued for the next flush. */
  readonly queued: number;
  /** Pubkeys in a request that has not resolved. */
  readonly inFlight: number;
  /** Pubkeys in failure cooldown. */
  readonly cooling: number;
}

/** The app-wide profile loader. One instance per account. */
export class DefaultProfileBatcher implements ProfileBatcher {
  private readonly loaded = new Set<Hex32>();
  private readonly inFlight = new Set<Hex32>();
  private readonly failedAt = new Map<Hex32, number>();
  /** Pubkeys whose last fetch returned some requested kinds but not all. */
  private readonly partialAt = new Map<Hex32, number>();
  private queued = new Set<Hex32>();
  private timer: ReturnType<typeof setTimeout> | undefined;
  private running: Promise<void> = Promise.resolve();
  private readonly now: () => number;

  constructor(private readonly options: ProfileBatcherOptions) {
    this.now = options.now ?? (() => Date.now());
  }

  /** Current queue and cache sizes. */
  stats(): ProfileBatcherStats {
    return {
      loaded: this.loaded.size,
      queued: this.queued.size,
      inFlight: this.inFlight.size,
      cooling: this.failedAt.size + this.partialAt.size,
    };
  }

  /**
   * Registers interest in some pubkeys. Returns immediately; results arrive in
   * the store, where the caller is already observing.
   *
   * Cheap enough to call from a render path — everything already known, in flight
   * or cooling down is discarded here rather than becoming network traffic.
   */
  request(pubkeys: readonly Hex32[]): void {
    let added = false;
    for (const pubkey of pubkeys) {
      if (pubkey === "" || this.loaded.has(pubkey)) continue;
      if (this.inFlight.has(pubkey) || this.queued.has(pubkey)) continue;
      if (this.isCoolingDown(pubkey)) continue;
      if (this.isPartialCoolingDown(pubkey)) continue;
      this.queued.add(pubkey);
      added = true;
    }
    if (added) this.schedule();
  }

  /** Runs the queue now and resolves once every in-flight request has settled. */
  async flush(): Promise<void> {
    if (this.timer !== undefined) {
      clearTimeout(this.timer);
      this.timer = undefined;
    }
    this.running = this.running.then(() => this.drain());
    await this.running;
  }

  /** Forgets everything. Call on account switch. */
  reset(): void {
    if (this.timer !== undefined) {
      clearTimeout(this.timer);
      this.timer = undefined;
    }
    this.loaded.clear();
    this.inFlight.clear();
    this.failedAt.clear();
    this.partialAt.clear();
    this.queued.clear();
  }

  private get kinds(): readonly number[] {
    return this.options.kinds ?? [KIND_METADATA, KIND_RELAY_LIST];
  }

  private isCoolingDown(pubkey: Hex32): boolean {
    const failed = this.failedAt.get(pubkey);
    if (failed === undefined) return false;
    const cooldown = this.options.failureCooldownMs ?? 60_000;
    if (this.now() - failed < cooldown) return true;
    this.failedAt.delete(pubkey);
    return false;
  }

  private isPartialCoolingDown(pubkey: Hex32): boolean {
    const partial = this.partialAt.get(pubkey);
    if (partial === undefined) return false;
    const cooldown = this.options.partialCooldownMs ?? 15 * 60_000;
    if (this.now() - partial < cooldown) return true;
    this.partialAt.delete(pubkey);
    return false;
  }

  private schedule(): void {
    if (this.timer !== undefined) return;
    this.timer = setTimeout(() => {
      this.timer = undefined;
      this.running = this.running.then(() => this.drain());
      void this.running.catch((error) => this.options.onError?.(error));
    }, this.options.debounceMs ?? 250);
  }

  private async drain(): Promise<void> {
    while (this.queued.size > 0) {
      const batch = [...this.queued];
      this.queued = new Set();
      const wanted = await this.dropAlreadyStored(batch);
      const chunkSize = Math.max(1, this.options.maxAuthorsPerFilter ?? 100);
      for (let i = 0; i < wanted.length; i += chunkSize) {
        await this.fetchChunk(wanted.slice(i, i + chunkSize));
      }
    }
  }

  /** Marks anything already fully stored as loaded without asking a relay. */
  private async dropAlreadyStored(
    pubkeys: readonly Hex32[],
  ): Promise<readonly Hex32[]> {
    if (pubkeys.length === 0) return [];
    const held = await this.heldKinds(pubkeys);
    const remaining: Hex32[] = [];
    for (const pubkey of pubkeys) {
      if (this.isComplete(held, pubkey)) this.loaded.add(pubkey);
      else remaining.push(pubkey);
    }
    return remaining;
  }

  /**
   * Which of the requested kinds the store already holds, per pubkey.
   *
   * Per kind rather than "has metadata", and that distinction was a silent,
   * self-reinforcing failure of the whole outbox model. This asked for kind 0
   * alone, so an author whose metadata had arrived counted as done — and their
   * kind-10002 was then never requested again, by anything, for the life of the
   * session. `OutboxRouter` reads relay lists out of this store and falls back to
   * the configured read set when it finds none, so every such author was read from
   * the reader's own four relays forever: an author who publishes to one relay of
   * their own showed a timeline months out of date, with no error anywhere and no
   * way to recover. The better a client got at loading avatars, the more
   * thoroughly it broke its own routing.
   *
   * One query per kind, which is two: the store's planner picks the author index
   * either way, and grouping per kind here is what makes completeness answerable.
   */
  private async heldKinds(
    pubkeys: readonly Hex32[],
  ): Promise<ReadonlyMap<Hex32, ReadonlySet<number>>> {
    const held = new Map<Hex32, Set<number>>();
    for (const kind of this.kinds) {
      const rows = await this.options.store.query({
        kinds: [kind],
        authors: [...pubkeys],
      });
      for (const row of rows) {
        const kinds = held.get(row.event.pubkey) ?? new Set<number>();
        kinds.add(kind);
        held.set(row.event.pubkey, kinds);
      }
    }
    return held;
  }

  /** True when every requested kind is held for this pubkey. */
  private isComplete(
    held: ReadonlyMap<Hex32, ReadonlySet<number>>,
    pubkey: Hex32,
  ): boolean {
    const kinds = held.get(pubkey);
    if (kinds === undefined) return false;
    return this.kinds.every((kind) => kinds.has(kind));
  }

  private async fetchChunk(chunk: readonly Hex32[]): Promise<void> {
    if (chunk.length === 0) return;
    for (const pubkey of chunk) this.inFlight.add(pubkey);
    try {
      const filters = await this.filtersFor(chunk);
      if (filters.length > 0) {
        await this.options.subscriptions.fetch({ filters });
      }
      const held = await this.heldKinds(chunk);
      const now = this.now();
      for (const pubkey of chunk) {
        if (this.isComplete(held, pubkey)) {
          this.loaded.add(pubkey);
          this.failedAt.delete(pubkey);
          this.partialAt.delete(pubkey);
          continue;
        }
        if (held.has(pubkey)) {
          /*
           * Some kinds arrived and some did not — in practice metadata without a
           * relay list, which is both common and worth retrying: plenty of authors
           * have never published a kind-10002, and plenty have one that only a
           * relay we did not happen to ask this time is holding.
           *
           * Its own, longer cooldown rather than the failure one. "No relay list
           * exists" is a stable fact about an author, so retrying it on the failure
           * cadence would spend a request a minute per visible author to re-learn
           * it; never retrying is how the bug above became permanent.
           */
          this.partialAt.set(pubkey, now);
          this.failedAt.delete(pubkey);
          continue;
        }
        // Nothing came back at all. Remember, so a missing profile does not become
        // an infinite retry loop driven by the render path.
        this.failedAt.set(pubkey, now);
      }
    } catch (error) {
      this.options.onError?.(error);
      const now = this.now();
      for (const pubkey of chunk) this.failedAt.set(pubkey, now);
    } finally {
      for (const pubkey of chunk) this.inFlight.delete(pubkey);
    }
  }

  /**
   * The bound for one profile request, with headroom.
   *
   * `authors × kinds` looks exact — every kind requested here is replaceable, so a
   * relay may legitimately hold only one event per (author, kind) — and it is
   * wrong against real relays. Some keep superseded versions and serve them all:
   * `purplepag.es` answers a two-event request for one author with two copies of
   * that author's kind 0, and the relay list the same request asked for is then
   * outside the limit and never sent. The multiplier buys room for that without
   * going unbounded, which still matters: an unbounded filter invites every old
   * version a relay ever kept.
   */
  private limitFor(authorCount: number): number {
    return Math.max(1, authorCount) * Math.max(1, this.kinds.length) * 4;
  }

  /**
   * Where to ask for a batch of profiles.
   *
   * Routed relays first, then every configured relay that routing did not name.
   * Routing alone is not enough here and the reason is specific to *these* kinds:
   * an author whose relay list we do not hold yet is routed to the fallback set
   * capped per author — three of four, in the default configuration — and the one
   * document that would end the guessing may be on the fourth. Relays are not
   * interchangeable for this: `purplepag.es` exists to hold kind 0 and kind 10002,
   * and a relay-list lookup that skips it to save a socket has saved nothing.
   *
   * These filters are small, bounded and issued once per author per session, so
   * asking the whole configured set costs almost nothing — and buys the difference
   * between reading an author from their own relays and reading them from ours.
   */
  private async filtersFor(
    chunk: readonly Hex32[],
  ): Promise<readonly RelayBasedFilter[]> {
    const configured = normalizeRelayUrls(this.options.relays);
    const router = this.options.router;
    const out: RelayBasedFilter[] = [];
    const asked = new Set<string>();

    if (router !== undefined) {
      const routed = await router.route(chunk, { kinds: [...this.kinds] });
      for (const rf of routed) {
        asked.add(rf.relay);
        // Routing splits the chunk per relay, so each filter is bounded by the
        // authors *it* asks for rather than by the whole batch.
        out.push({
          relay: rf.relay,
          filter: {
            ...rf.filter,
            limit: this.limitFor(rf.filter.authors?.length ?? chunk.length),
          },
        });
      }
    }

    const filter = {
      kinds: [...this.kinds],
      authors: [...chunk],
      limit: this.limitFor(chunk.length),
    };
    for (const relay of configured) {
      if (asked.has(relay)) continue;
      out.push({ relay, filter });
    }
    return out;
  }
}
